import { useId, useRef, useState } from 'react'

import type { FramePoint, HoleMap as HoleMapData, HoleShot, MapLayer } from '../api/public'

import {
  holeLine,
  leftToPin,
  lieWords,
  MIN_ASPECT,
  PAD_M,
  shotLength,
  type MapPlayer,
  type MapView,
} from './holeGeometry'

/**
 * One hole drawn from above, tee at the bottom and the green at the top, with
 * whatever shots were played on it.
 *
 * # One frame in, one rotation out
 *
 * Everything arrives in the course FRAME (metres, x east, y north): the server
 * has already converted the real-world shapes. So the whole view is a single
 * rotation that points the tee-to-green line up the page, applied to every
 * point here in plain arithmetic -- and the aerial photo, which needs pixels
 * placed rather than points moved, gets the same rotation composed with its own
 * placement as one SVG transform.
 *
 * # A long hole is not a sliver
 *
 * The view is kept at least 0.42 as wide as it is long (the rule the shot-map
 * exploration settled on), so a par 5 still shows the ground either side of its
 * fairway rather than a strip one shot wide.
 *
 * # Drawn or aerial, never both
 *
 * The drawn view paints the shapes; the aerial view paints the photo and keeps
 * only the course's own penalty and out-of-bounds lines over it, because the
 * photo already shows the rest and two outlines of one bunker read as a fault.
 */

export function HoleMap({
  map,
  view,
  tee,
  pin,
  players = [],
  label,
  compact = false,
}: {
  map: HoleMapData | null
  view: MapView
  tee?: string
  pin?: FramePoint | null
  players?: MapPlayer[]
  label: string
  compact?: boolean
}) {
  const { from, to } = holeLine(map, tee, pin ?? null, players)
  // The shot under the pointer, and where on the figure to put its card.
  const [hover, setHover] = useState<{
    player: MapPlayer
    shot: HoleShot
    x: number
    y: number
  } | null>(null)
  const figRef = useRef<HTMLElement>(null)
  const clipId = `hole-clip-${useId().replace(/:/g, '')}`
  if (!from || !to) {
    return <p className="note">This hole cannot be drawn: it has no tee and no green on record.</p>
  }

  // The rotation: `ahead` points from tee to green, `right` is the golfer's
  // right. Screen x is distance right of the line; screen y runs DOWN the page,
  // so it is minus the distance ahead.
  let fx = to[0] - from[0]
  let fy = to[1] - from[1]
  const n = Math.hypot(fx, fy) || 1
  fx /= n
  fy /= n
  const local = (p: FramePoint): [number, number] => {
    const dx = p[0] - from[0]
    const dy = p[1] - from[1]
    return [dx * fy - dy * fx, -(dx * fx + dy * fy)]
  }

  // What must be in view: the hole's own points and every shot.
  const keep: FramePoint[] = [from, to]
  if (map) {
    keep.push(...map.aim_points)
    if (map.green_center) keep.push(map.green_center)
  }
  for (const p of players) for (const s of p.shots) keep.push(s.from, s.to, ...s.path)
  const ls = keep.map(local)
  let minX = Math.min(...ls.map((p) => p[0])) - PAD_M
  let maxX = Math.max(...ls.map((p) => p[0])) + PAD_M
  const minY = Math.min(...ls.map((p) => p[1])) - PAD_M
  const maxY = Math.max(...ls.map((p) => p[1])) + PAD_M
  const h = maxY - minY
  if (maxX - minX < h * MIN_ASPECT) {
    const mid = (minX + maxX) / 2
    minX = mid - (h * MIN_ASPECT) / 2
    maxX = mid + (h * MIN_ASPECT) / 2
  }
  const w = maxX - minX
  // A marker size in metres that reads as a few pixels at any size. Taken from
  // the LONGER side as well as the width: a long par 5 is scaled to fit its
  // height, so a width-based unit made its markers smallest exactly where the
  // hole is longest.
  const u = Math.max(w, h * 0.55) / 260
  // A compact card shrinks a whole hole into a couple of hundred pixels, so its
  // tee and pin are drawn larger for the same glance to find them.
  const m = compact ? u * 1.7 : u

  const pts = (ps: FramePoint[]) =>
    ps
      .map(local)
      .map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`)
      .join(' ')

  const layers = map?.layers ?? []
  const aerial = view === 'aerial' ? map?.views.aerial : null

  // The photo: its pixels -> the frame (the server's placement), then the
  // frame -> this view (the rotation above). Both are affine, so one matrix.
  let photoTransform = ''
  if (aerial) {
    const [a, b, c, d, e, f] = aerial.transform
    // local(x, y) = R * (x - from) with R = [[fy, -fx], [-fx, -fy]]
    const r = [fy, -fx, -fx, -fy]
    const ta = r[0] * a + r[1] * b
    const tb = r[2] * a + r[3] * b
    const tc = r[0] * c + r[1] * d
    const td = r[2] * c + r[3] * d
    const [te, tf] = local([e, f])
    photoTransform = `matrix(${ta} ${tb} ${tc} ${td} ${te} ${tf})`
  }

  const drawn = (kind: MapLayer['kind']) => layers.filter((l) => l.kind === kind)
  const shape = (l: MapLayer, i: number, cls: string) =>
    l.kind === 'tree' && l.points.length === 1 ? (
      <circle
        key={`${cls}-${i}`}
        className={cls}
        cx={local(l.points[0])[0]}
        cy={local(l.points[0])[1]}
        r={4.5}
      />
    ) : l.closed ? (
      <polygon key={`${cls}-${i}`} className={cls} points={pts(l.points)} />
    ) : (
      <polyline key={`${cls}-${i}`} className={cls} points={pts(l.points)} />
    )

  const scaleM = 50 * 0.9144
  const [tx, ty] = local(from)
  const [px, py] = local(to)

  // A shot's card opens on a CLICK (or a tap, or Enter) and stays until the same
  // shot or the map is clicked again: hover does not exist on a phone, and a
  // card that vanishes as the pointer moves cannot be read on a desktop either.
  // It sits beside where the shot was chosen, kept inside the figure so it never
  // pushes the page sideways at phone width.
  const choose = (at: { clientX: number; clientY: number }, player: MapPlayer, shot: HoleShot) => {
    if (hover && hover.player.name === player.name && hover.shot.shot_number === shot.shot_number) {
      setHover(null)
      return
    }
    const box = figRef.current?.getBoundingClientRect()
    if (!box) return
    const x = Math.min(Math.max(at.clientX - box.left + 12, 4), Math.max(box.width - 196, 4))
    const y = Math.max(at.clientY - box.top - 70, 4)
    setHover({ player, shot, x, y })
  }
  const isChosen = (player: MapPlayer, shot: HoleShot) =>
    !!hover && hover.player.name === player.name && hover.shot.shot_number === shot.shot_number

  return (
    <figure className={`hole-map${compact ? ' hole-map-compact' : ''}`} ref={figRef}>
      <svg
        className="hole-map-svg"
        viewBox={`${minX.toFixed(1)} ${minY.toFixed(1)} ${w.toFixed(1)} ${h.toFixed(1)}`}
        role="group"
        aria-label={label}
        data-testid="hole-map"
        data-view={aerial ? 'aerial' : 'drawn'}
        onClick={() => setHover(null)}
      >
        {/* Everything is clipped to the hole's own box. Where the screen is a
            different shape from the hole, the SVG leaves room either side, and
            anything drawn past the box -- a long out-of-bounds line, the edge
            of the photo -- would otherwise show in that margin. */}
        <defs>
          <clipPath id={clipId}>
            <rect x={minX} y={minY} width={w} height={h} />
          </clipPath>
        </defs>
        <g clipPath={`url(#${clipId})`}>
          <rect className="hm-ground" x={minX} y={minY} width={w} height={h} />
          {aerial ? (
            <image
              href={aerial.src}
              width={aerial.width}
              height={aerial.height}
              transform={photoTransform}
              preserveAspectRatio="none"
              data-testid="hole-map-aerial"
            />
          ) : (
            <g className="hm-drawn">
              {drawn('wood').map((l, i) => shape(l, i, 'hm-wood'))}
              {drawn('water').map((l, i) => shape(l, i, 'hm-water'))}
              {drawn('fairway').map((l, i) => shape(l, i, 'hm-fairway'))}
              {drawn('tee').map((l, i) => shape(l, i, 'hm-tee'))}
              {drawn('green').map((l, i) => shape(l, i, 'hm-green'))}
              {drawn('bunker').map((l, i) => shape(l, i, 'hm-bunker'))}
              {drawn('path').map((l, i) => shape(l, i, 'hm-path'))}
              {drawn('tree').map((l, i) => shape(l, i, 'hm-tree'))}
            </g>
          )}
          {/* The course's own lines, over either view. A penalty area is an
            outline, never a fill: its stakes sit back from the water, and a
            filled penalty polygon put a second shore beside the real one. */}
          {drawn('penalty').map((l, i) => shape(l, i, 'hm-penalty'))}
          {drawn('out_of_bounds').map((l, i) => shape(l, i, 'hm-oob'))}

          {players.map((p) => (
            <g key={p.name} className={`series-${p.series % 5} hm-player`}>
              {p.shots.map((s) => {
                const line = s.path.length >= 2 ? [...s.path, s.to] : [s.from, s.to]
                const [sx, sy] = local(s.from)
                const [ex, ey] = local(s.to)
                return (
                  <g
                    key={s.shot_number}
                    data-testid={`shot-${p.name}-${s.shot_number}`}
                    aria-label={shotWords(p.name, s, pin ?? null)}
                    className={isChosen(p, s) ? 'hm-shot-chosen' : undefined}
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation()
                      choose(e, p, s)
                    }}
                    onKeyDown={(e) => {
                      if (e.key !== 'Enter' && e.key !== ' ') return
                      e.preventDefault()
                      const r = e.currentTarget.getBoundingClientRect()
                      choose({ clientX: r.left + r.width / 2, clientY: r.top + r.height / 2 }, p, s)
                    }}
                  >
                    {/* A wide invisible stroke to catch the click: the drawn line
                      is three pixels, which no thumb can find. */}
                    <polyline className="hm-shot-hit" points={pts(line)} />
                    <polyline className="hm-shot-casing" points={pts(line)} />
                    <polyline className="hm-shot" points={pts(line)} />
                    <circle className="hm-shot-from" cx={sx} cy={sy} r={u * 3.2} />
                    {s.penalty && (
                      <path
                        className="hm-penalty-mark"
                        d={`M${ex - u * 4},${ey - u * 4} l${u * 8},${u * 8} m0,${-u * 8} l${-u * 8},${u * 8}`}
                      />
                    )}
                  </g>
                )
              })}
            </g>
          ))}

          {/* The two ends of the hole, drawn to be found at a glance on grass,
            water or photo: each in white over a dark casing, like the shots. */}
          <g className="hm-tee-mark" data-testid="hole-map-tee">
            <circle cx={tx} cy={ty} r={m * 7} />
            <text x={tx} y={ty} fontSize={m * 8.5} textAnchor="middle" dominantBaseline="central">
              T
            </text>
            <title>Tee</title>
          </g>
          {pin ? (
            <g className="hm-pin" data-testid="hole-map-pin">
              <line className="hm-pin-casing" x1={px} y1={py} x2={px} y2={py - m * 30} />
              <line className="hm-pin-pole" x1={px} y1={py} x2={px} y2={py - m * 30} />
              <path
                className="hm-pin-flag"
                d={`M${px},${py - m * 30} l${m * 15},${m * 5.5} l${-m * 15},${m * 5.5}z`}
              />
              <circle className="hm-pin-cup" cx={px} cy={py} r={m * 3.5} />
              <title>Pin</title>
            </g>
          ) : (
            <g className="hm-green-mark" data-testid="hole-map-green">
              <circle className="hm-green-ring" cx={px} cy={py} r={m * 8} />
              <circle className="hm-green-dot" cx={px} cy={py} r={m * 2} />
              <title>Centre of the green</title>
            </g>
          )}
        </g>
        <g className="hm-scale">
          <rect
            x={minX + u * 6}
            y={maxY - u * 20}
            width={scaleM + u * 12}
            height={u * 15}
            rx={u * 2}
          />
          <line
            x1={minX + u * 12}
            y1={maxY - u * 9}
            x2={minX + u * 12 + scaleM}
            y2={maxY - u * 9}
          />
          <text x={minX + u * 12} y={maxY - u * 12} fontSize={u * 7}>
            50 yd
          </text>
        </g>
      </svg>
      {hover && (
        <div
          className={`hole-map-card series-${hover.player.series % 5}`}
          style={{ left: hover.x, top: hover.y }}
          role="status"
          data-testid="shot-card"
        >
          <strong>
            <span className="swatch" aria-hidden="true" />
            {hover.player.name} · shot {hover.shot.shot_number}
          </strong>
          <span>
            {hover.shot.club ?? 'Club not recorded'} · {shotLength(hover.shot)}
          </span>
          <span className="note">
            {lieWords(hover.shot.lie_from)} → {lieWords(hover.shot.lie_to)}
            {hover.shot.penalty && ' · penalty'}
            {leftToPin(hover.shot, pin ?? null) && ` · ${leftToPin(hover.shot, pin ?? null)} left`}
          </span>
        </div>
      )}
      {map && !compact && <MapCredit map={map} aerial={!!aerial} />}
    </figure>
  )
}

/** Who the picture came from, as the producer said -- OpenStreetMap's licence requires it. */
function MapCredit({ map, aerial }: { map: HoleMapData; aerial: boolean }) {
  const sources = map.attribution.map((a) => a.source).filter((s): s is string => !!s)
  const notes: string[] = []
  if (sources.length) notes.push(`Map data: ${sources.join(', ')}.`)
  if (map.fit_rms_m) notes.push(`Real-world shapes placed to within about ${map.fit_rms_m} m.`)
  if (aerial && !sources.some((s) => /imagery|naip|usda/i.test(s)))
    notes.push('Aerial imagery as supplied.')
  if (!notes.length) return null
  return <figcaption className="hole-map-credit">{notes.join(' ')}</figcaption>
}

/** One shot in words: what a screen reader hears, and what the card shows. */
function shotWords(name: string, s: HoleShot, pin: FramePoint | null): string {
  const left = leftToPin(s, pin)
  return `${name}, shot ${s.shot_number}${s.club ? `, ${s.club}` : ''}, ${shotLength(s)}${
    s.lie_from ? ` from the ${s.lie_from}` : ''
  }${s.lie_to ? ` to the ${s.lie_to}` : ''}${s.penalty ? ', penalty' : ''}${left ? `, ${left} left` : ''}`
}
